import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { Goal, GoalStatus, GoalCategory } from '../../types/Goal';
import { goalService } from '../../services/goalService';
import { metricService, Metric } from '../../services/metricService';
import Sidebar from '../Sidebar';
import './GoalEdit.css';

interface GoalEditProps {
  user: User | null;
  // Props for when used as routed page
  sidebarOpen?: boolean;
  onToggleSidebar?: () => void;
  onSignOut?: () => void;
  onShowAuthModal?: () => void;
  authLoading?: boolean;
}

const GoalEdit: React.FC<GoalEditProps> = ({
  user,
  sidebarOpen = false,
  onToggleSidebar,
  onSignOut,
  onShowAuthModal,
  authLoading = false
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [goal, setGoal] = useState<Goal | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<GoalCategory>(GoalCategory.PERSONAL);
  const [status, setStatus] = useState<GoalStatus>(GoalStatus.NOT_STARTED);
  const [targetDate, setTargetDate] = useState('');
  const [startedDate, setStartedDate] = useState('');
  const [metricName, setMetricName] = useState('');
  const [targetMetricValue, setTargetMetricValue] = useState('');
  const [notes, setNotes] = useState('');

  // Available metrics
  const [availableMetrics, setAvailableMetrics] = useState<Metric[]>([]);

  // Load goal data
  useEffect(() => {
    if (user && id) {
      loadGoal();
      loadAvailableMetrics();
    }
  }, [user, id]);

  // Helper function to format dates for input fields
  const formatDateForInput = (dateString: string | undefined | null): string => {
    if (!dateString) return '';
    
    try {
      // If it's already in YYYY-MM-DD format, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }
      
      // Otherwise, parse and format
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  const loadGoal = async () => {
    if (!user || !id) return;

    setLoading(true);
    try {
      const response = await goalService.getGoalById(id);

      if (response.success && response.goal) {
        const convertedGoal = goalService.convertFirebaseGoal(response.goal);
        setGoal(convertedGoal);

        // Populate form fields
        setTitle(convertedGoal.title);
        setDescription(convertedGoal.description || '');
        setCategory(convertedGoal.category);
        setStatus(convertedGoal.status);
        setTargetDate(formatDateForInput(convertedGoal.targetDate));
        setStartedDate(formatDateForInput(convertedGoal.startedDate));
        setMetricName(convertedGoal.metric_name || '');
        setTargetMetricValue(convertedGoal.target_metric_value?.toString() || '');
        setNotes(convertedGoal.notes || '');

        setError(null);
      } else {
        setError('Goal not found');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load goal');
      console.error('Error loading goal:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableMetrics = async () => {
    try {
      const response = await metricService.getAllMetrics({ is_active: true });

      if (response.success && response.metrics) {
        setAvailableMetrics(response.metrics);
      }
    } catch (error) {
      console.error('Error loading available metrics:', error);
    }
  };

  const handleSave = async () => {
    if (!goal || !user) return;

    if (!title.trim()) {
      setError('Goal title is required');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const updateData: any = {
        id: goal.id,
        title: title.trim(),
        description: description.trim(),
        category,
        status,
        target_date: targetDate || null,
        started_date: startedDate || null,
        metric_name: metricName ? metricName : null,
        target_metric_value: targetMetricValue ? parseFloat(targetMetricValue) : null,
        notes: notes.trim() || undefined
      };

      const result = await goalService.updateGoal(updateData);

      if (result.success) {
        setSuccessMessage('Goal updated successfully!');
        
        // Navigate back to goal detail after a short delay
        setTimeout(() => {
          navigate(`/goals/${goal.id}`, { replace: true });
        }, 1000);
      } else {
        throw new Error('Failed to update goal');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update goal');
      console.error('Error updating goal:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/goals/${id}`, { replace: true });
  };

  // Early returns
  if (!user) {
    return (
      <div className="App fullscreen-goals">
        <Sidebar
          user={user}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={onToggleSidebar || (() => {})}
          onSignOut={onSignOut || (() => {})}
          onShowAuthModal={onShowAuthModal || (() => {})}
          authLoading={authLoading}
          currentPath="/goals"
        />
        <div className="goal-edit-page">
          <div className="goal-edit-header">
            <div className="header-left">
              <button className="menu-btn" onClick={onToggleSidebar || (() => {})}>☰</button>
            </div>
            <h1>Edit Goal</h1>
          </div>
          <div className="auth-required">
            <p>Please sign in to edit goals.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="App fullscreen-goals">
        <Sidebar
          user={user}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={onToggleSidebar || (() => {})}
          onSignOut={onSignOut || (() => {})}
          onShowAuthModal={onShowAuthModal || (() => {})}
          authLoading={authLoading}
          currentPath="/goals"
        />
        <div className="goal-edit-page">
          <div className="goal-edit-header">
            <div className="header-left">
              <button className="menu-btn" onClick={onToggleSidebar || (() => {})}>☰</button>
            </div>
            <h1>Loading...</h1>
          </div>
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading goal...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !goal) {
    return (
      <div className="App fullscreen-goals">
        <Sidebar
          user={user}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={onToggleSidebar || (() => {})}
          onSignOut={onSignOut || (() => {})}
          onShowAuthModal={onShowAuthModal || (() => {})}
          authLoading={authLoading}
          currentPath="/goals"
        />
        <div className="goal-edit-page">
          <div className="goal-edit-header">
            <div className="header-left">
              <button className="menu-btn" onClick={onToggleSidebar || (() => {})}>☰</button>
            </div>
            <h1>Goal Not Found</h1>
          </div>
          <div className="error-container">
            <p>{error}</p>
            <button onClick={() => navigate('/goals')} className="back-to-goals-btn">
              Back to Goals
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App fullscreen-goals">
      <Sidebar
        user={user}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={onToggleSidebar || (() => {})}
        onSignOut={onSignOut || (() => {})}
        onShowAuthModal={onShowAuthModal || (() => {})}
        authLoading={authLoading}
        currentPath="/goals"
      />
      <div className="goal-edit-page">
        <div className="goal-edit-header">
          <h1>Edit Goal</h1>
        </div>

        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {successMessage && (
          <div className="success-banner">
            <span>{successMessage}</span>
            <button onClick={() => setSuccessMessage(null)}>×</button>
          </div>
        )}

        <div className="goal-edit-content">
          <div className="edit-form">
            {/* Basic Information Section */}
            <div className="form-section">
              <h2>Basic Information</h2>

              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter goal title..."
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe your goal..."
                  rows={4}
                  className="form-textarea"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value as GoalCategory)}
                    className="form-select"
                  >
                    {Object.values(GoalCategory).map(cat => (
                      <option key={cat} value={cat}>
                        {cat.replace('_', ' ').toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as GoalStatus)}
                    className="form-select"
                  >
                    {Object.values(GoalStatus).map(st => (
                      <option key={st} value={st}>
                        {st.replace('_', ' ').toUpperCase()}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Dates Section */}
            <div className="form-section">
              <h2>Timeline</h2>

              <div className="form-row">
                <div className="form-group">
                  <label>Started Date</label>
                  <input
                    type="date"
                    value={startedDate}
                    onChange={(e) => setStartedDate(e.target.value)}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Target Date</label>
                  <input
                    type="date"
                    value={targetDate}
                    onChange={(e) => setTargetDate(e.target.value)}
                    className="form-input"
                  />
                </div>
              </div>
            </div>

            {/* Metric Tracking Section */}
            <div className="form-section">
              <h2>Metric Tracking</h2>
              <p className="form-section-description">
                Link a metric to automatically calculate progress based on measurable data.
              </p>

              <div className="form-group">
                <label>Linked Metric (Optional)</label>
                <select
                  value={metricName}
                  onChange={(e) => setMetricName(e.target.value)}
                  className="form-select"
                >
                  <option value="">No metric</option>
                  {availableMetrics.map(metric => (
                    <option key={metric.id} value={metric.name}>
                      {metric.name} ({metric.category})
                    </option>
                  ))}
                </select>
              </div>

              {metricName && (
                <div className="form-group">
                  <label>Target Value</label>
                  <input
                    type="number"
                    value={targetMetricValue}
                    onChange={(e) => setTargetMetricValue(e.target.value)}
                    placeholder="Enter target value..."
                    className="form-input"
                    step="any"
                  />
                  <p className="form-hint">
                    Set your target value for the selected metric.
                  </p>
                </div>
              )}
            </div>

            {/* Notes Section */}
            <div className="form-section">
              <h2>Additional Notes</h2>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any additional notes or context..."
                  rows={4}
                  className="form-textarea"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="form-actions">
              <button
                type="button"
                onClick={handleCancel}
                className="cancel-btn"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="save-btn"
                disabled={saving || !title.trim()}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoalEdit;

